import {useQuery} from "@apollo/client";
import classNames from "classnames";
import pluralize from "pluralize";
import {memo, useCallback, useState} from "react";

import {Accordion} from "@/components/accordion/Accordion.tsx";
import {Card} from "@/components/card/Card.tsx";
import {MultiServiceSelect} from "@/components/multiServiceSelect/MultiServiceSelect.tsx";
import {MultiServiceSelectCardProps} from "@/components/MultiServiceSelectCard/MultiServiceSelectCard.types.ts";
import {GQL_TOTAL_CHECKS} from "@/lib/queries/checks.ts";

import styles from "./MultiServiceSelectCard.module.scss";

interface TotalChecksResponse {
  values: {
    total: number;
  };
}

export const MultiServiceSelectCard = memo(
  ({
    title = "Monitoring Checks",
    className,
    defaultValue,
    onAppend,
    onReplace,
    ...multiServiceSelectProps
  }: MultiServiceSelectCardProps) => {
    const [selectedCount, setSelectedCount] = useState(defaultValue?.length || 0);
    const {data} = useQuery<TotalChecksResponse>(GQL_TOTAL_CHECKS, {variables: {limit: 0}});
    const totalChecks = data?.values.total;

    const handleAppend = useCallback(
      (value: number) => {
        setSelectedCount((prev) => prev + 1);
        onAppend(value);
      },
      [onAppend]
    );

    const handleReplace = useCallback(
      (values: number[]) => {
        setSelectedCount(values.length);
        onReplace(values);
      },
      [onReplace]
    );

    const subtitle =
      totalChecks != null
        ? `${selectedCount} / ${totalChecks} ${pluralize("Check", totalChecks)} selected`
        : `${pluralize("Check", selectedCount, true)} selected`;

    return (
      <Card variant="accordion" className={classNames(styles.multiServiceSelectCard, className)}>
        <Accordion>
          <Accordion.Item eventKey="0">
            <Accordion.Header className={styles.header}>
              <div className={"d-flex flex-column gap-1"}>
                <div className={classNames("pulseTextBodyBold")}>{title}</div>
                <div className={classNames("pulseTextBodySmall pulse-color-gray-600", styles.subtitle)}>{subtitle}</div>
              </div>
            </Accordion.Header>
            <Accordion.Body className={styles.body}>
              <MultiServiceSelect
                {...multiServiceSelectProps}
                defaultValue={defaultValue}
                onAppend={handleAppend}
                onReplace={handleReplace}
              />
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Card>
    );
  }
);

MultiServiceSelectCard.displayName = "MultiServiceSelectCard";
